import axios from 'axios';

const state = {
  favorites: [],
  currentPageFavorite: null,
  loading: false
};

const getters = {
  favorites: state => state.favorites,
  isCurrentPageFavorited: state => !!state.currentPageFavorite,
  currentPageFavorite: state => state.currentPageFavorite,
  // Nuovo getter per controllo locale se un URL è nei preferiti
  checkIfUrlIsFavorited: state => (url) => {
    return state.favorites.find(fav => fav.url === url) || null;
  }
};

const actions = {
  async fetchFavorites({ commit }) {
    commit('SET_LOADING', true);
    try {
      const response = await axios.get('/favorites');
      commit('SET_FAVORITES', response.data.favorites);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async addFavorite({ commit }, favoriteData) {
    try {
      const response = await axios.post('/favorites', favoriteData);
      const newFavorite = response.data.favorite;

      // Aggiorna lo stato locale invece di ri-fetchare tutto
      commit('ADD_FAVORITE', newFavorite);
      commit('SET_CURRENT_PAGE_FAVORITE', newFavorite);

      return newFavorite;
    } catch (error) {
      console.error('Error adding favorite:', error);
      throw error;
    }
  },

  async removeFavoriteById({ commit, state }, id) {
    try {
      await axios.delete(`/favorites/${id}`);

      // Aggiorna lo stato locale invece di ri-fetchare tutto
      commit('REMOVE_FAVORITE', id);

      // Solo resetta currentPageFavorite se è quello che stiamo rimuovendo
      if (state.currentPageFavorite && state.currentPageFavorite.id === id) {
        commit('SET_CURRENT_PAGE_FAVORITE', null);
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
      throw error;
    }
  },

  async checkCurrentPage({ commit, getters, state }, url) {

    // Rimuovo i query params per il controllo, così da non avere problemi con URL dinamici (es. dettagli item)
    const urlWithoutQuery = url.split('?')[0];
    // Prima controlla localmente usando il getter
    const localFavorite = getters.checkIfUrlIsFavorited(urlWithoutQuery);
    commit('SET_CURRENT_PAGE_FAVORITE', localFavorite);

    // Chiamata al backend solo se la lista dei preferiti è vuota (primo caricamento)
    // o se esplicitamente richiesto per sincronizzazione
    if (state.favorites.length === 0) {
      try {
        const response = await axios.post('/favorites/check', { url: urlWithoutQuery });
        commit('SET_CURRENT_PAGE_FAVORITE', response.data.favorite);
      } catch (error) {
        console.error('Error checking current page:', error);
      }
    }
  },

  async reorderFavorites({ commit, state }, reorderedPositions) {
    try {
      // Invia al backend le nuove posizioni (formato che il backend si aspetta)
      await axios.put('/favorites/reorder', { favorites: reorderedPositions });

      // Riordina i preferiti locali basandosi sulle nuove posizioni
      const updatedFavorites = [...state.favorites];
      updatedFavorites.sort((a, b) => {
        const itemA = reorderedPositions.find(p => p.id === a.id);
        const itemB = reorderedPositions.find(p => p.id === b.id);
        const posA = itemA ? itemA.position : 999;
        const posB = itemB ? itemB.position : 999;
        return posA - posB;
      });

      // Aggiorna lo stato locale con i preferiti riordinati (mantenendo tutti i dati)
      commit('UPDATE_FAVORITES_ORDER', updatedFavorites);
    } catch (error) {
      console.error('Error reordering favorites:', error);
      throw error;
    }
  },

  async updateFavoriteTitle({ commit }, { id, title }) {
    try {
      await axios.put(`/favorites/${id}`, { title });

      // Aggiorna lo stato locale invece di ri-fetchare tutto
      commit('UPDATE_FAVORITE', { id, updates: { title } });
    } catch (error) {
      console.error('Error updating favorite title:', error);
      throw error;
    }
  }
};

const mutations = {
  SET_FAVORITES(state, favorites) {
    state.favorites = favorites;
  },

  SET_CURRENT_PAGE_FAVORITE(state, favorite) {
    state.currentPageFavorite = favorite;
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  // Nuove mutations per operazioni locali
  ADD_FAVORITE(state, favorite) {
    state.favorites.push(favorite);
  },

  REMOVE_FAVORITE(state, id) {
    const index = state.favorites.findIndex(fav => fav.id === id);
    if (index !== -1) {
      state.favorites.splice(index, 1);
    }
  },

  UPDATE_FAVORITE(state, { id, updates }) {
    const favorite = state.favorites.find(fav => fav.id === id);
    if (favorite) {
      Object.assign(favorite, updates);
    }
  },

  UPDATE_FAVORITES_ORDER(state, orderedFavorites) {
    state.favorites = orderedFavorites;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};